import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Host, Optional, Self, SkipSelf } from '@angular/core';
import { ControlContainer, FormBuilder, NgControl } from '@angular/forms';
import { PagedSelectComponent } from '@components/paged-select/paged-select.component';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import Computer, { AgentType, GetComputersParams } from 'mbs-ui-shared@models/Computer';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { BehaviorSubject, Observable, debounceTime, map, of, switchMap, tap } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'mbs-rmm-computer-select',
  templateUrl: './paged-select.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RmmComputerSelectComponent extends PagedSelectComponent<Computer> {
  protected bindLabel = 'name';
  protected bindValue = 'hid';
  public placeholder = this.i18nPipe.transform('shared:captions:computers');
  private computers$ = new BehaviorSubject<Computer[]>([]);
  private loading$ = new BehaviorSubject<boolean>(false);
  private loaded$ = new BehaviorSubject<boolean>(false);

  private offset = 0;
  private limit = 50;
  private total = 0;

  private currentSearchValue = null;

  constructor(
    @Optional() @Host() @SkipSelf() public controlContainer: ControlContainer,
    @Optional() @Self() protected ngControl: NgControl,
    private facade: ComputersFacade,
    protected fb: FormBuilder,
    protected cdr: ChangeDetectorRef,
    private i18nPipe: I18NextPipe
  ) {
    super(controlContainer, ngControl, fb, cdr);
    this.setupSearchProcess();
  }

  protected loadData(): void {
    if (this.loaded$.value && this.total <= this.computers$.value.length) return;

    this.loading$.next(true);

    const payload: GetComputersParams = {
      offline: false,
      appIds: [AgentType.RMM],
      offset: this.offset,
      limit: this.limit
    };
    if (this.currentSearchValue) payload.search = this.currentSearchValue;

    this.facade
      .getComputers(payload)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          this.total = response.total;
          this.offset++;
          this.computers$.next([...this.computers$.value.concat(response.data)]);
          this.loading$.next(false);
          this.loaded$.next(true);
        },
        error: () => this.loading$.next(false)
      });
  }

  protected get getData$(): Observable<Computer[]> {
    return this.computers$;
  }

  protected loadById(id: string): void {
    this.facade.loadComputerByHid({ hid: id, force: true });
  }

  protected getById$(id: string): Observable<Computer> {
    return this.facade.getByHid(id).pipe(
      map((computer) => {
        if (computer.online && Computer.hasAgent(computer, AgentType.RMM)) return computer;

        return null;
      })
    );
  }

  protected get allLoaded$(): Observable<boolean> {
    return of(this.loaded$.value && this.total <= this.computers$.value.length);
  }

  protected get loadingData$(): Observable<boolean> {
    return this.loading$;
  }

  protected get loadingById$(): Observable<boolean> {
    return this.facade.computerLoading$;
  }

  protected areItemsTheSame(selectedItem: Computer, item: Computer): boolean {
    return selectedItem.hid === item.hid;
  }

  setupSearchProcess() {
    this.typeahead$
      .pipe(
        debounceTime(600),
        tap((value) => this.loading$.next(true)),
        map((value) => {
          this.currentSearchValue = value?.trim() ? value : null;
          this.offset = 0;
          const payload: GetComputersParams = {
            search: value,
            offline: false,
            appIds: [AgentType.RMM],
            offset: this.offset,
            limit: this.limit
          };
          if (value) payload.search = value;
          return payload;
        }),
        switchMap((payload) => this.facade.getComputers(payload)),
        untilDestroyed(this)
      )
      .subscribe({
        next: (response) => {
          this.total = response.total;
          this.offset++;
          this.computers$.next([...response.data]);
          this.loading$.next(false);
          this.loaded$.next(true);
        },
        error: () => this.loading$.next(false)
      });
  }
}
